import { useEffect, useRef, useState } from 'react';
import {
  buildingIngestionApi,
  type BuildingsClearResponse,
  type BuildingsStatusResponse,
} from '../../spatial/services/building-ingestion-api';
import './ProjectBuildingIngestion.css';

// ─── Tipos locales ────────────────────────────────────────────────────────────

type UploadState = 'idle' | 'uploading' | 'polling' | 'done' | 'error';

interface LocalState {
  uploadState: UploadState;
  file: File | null;
  clearPrevious: boolean;
  status: BuildingsStatusResponse | null;
  lastClear: BuildingsClearResponse | null;
  errorMsg: string | null;
}

const POLLING_INTERVAL_MS = 3000;
const ACTIVE_STATUSES = new Set(['queued', 'processing']);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusLabel(s: BuildingsStatusResponse['status']): string {
  const labels: Record<BuildingsStatusResponse['status'], string> = {
    none: 'Sin datos',
    queued: 'En cola',
    processing: 'Procesando...',
    ready: 'Completado',
    error: 'Error',
  };
  return labels[s] ?? s;
}

function statusClass(s: BuildingsStatusResponse['status']): string {
  const classes: Record<BuildingsStatusResponse['status'], string> = {
    none: 'bi-status--none',
    queued: 'bi-status--active',
    processing: 'bi-status--active',
    ready: 'bi-status--ready',
    error: 'bi-status--error',
  };
  return classes[s] ?? '';
}

function formatMs(ms: unknown): string {
  if (typeof ms !== 'number') return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ProjectBuildingIngestionProps {
  projectId: string;
}

/**
 * ProjectBuildingIngestion — Sección de ingesta edilicia en el formulario de proyecto
 *
 * @remarks
 * Permite al SuperAdmin subir un CSV/CSV.GZ con centroides de edificios,
 * monitorear el estado del job y limpiar los datos existentes.
 *
 * Solo visible en modo edición (requiere projectId existente).
 */
export function ProjectBuildingIngestion({ projectId }: ProjectBuildingIngestionProps): JSX.Element {
  const [state, setState] = useState<LocalState>({
    uploadState: 'idle',
    file: null,
    clearPrevious: false,
    status: null,
    lastClear: null,
    errorMsg: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Carga inicial del status ──────────────────────────────────────────────
  useEffect(() => {
    void fetchStatus();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // ── Arrancar/detener polling según estado del job ─────────────────────────
  useEffect(() => {
    if (state.uploadState === 'polling') {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.uploadState]);

  // ── API calls ─────────────────────────────────────────────────────────────

  async function fetchStatus(): Promise<void> {
    try {
      const status = await buildingIngestionApi.getStatus(projectId);
      setState(prev => {
        // Si el job ya está activo (ej: recarga de página), activar polling
        const shouldPoll = ACTIVE_STATUSES.has(status.status) && prev.uploadState === 'idle';
        return {
          ...prev,
          status,
          uploadState: shouldPoll ? 'polling' : prev.uploadState,
        };
      });
    } catch {
      // No mostrar error en carga inicial silenciosa
    }
  }

  function startPolling(): void {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const status = await buildingIngestionApi.getStatus(projectId);
        setState(prev => {
          const stillActive = ACTIVE_STATUSES.has(status.status);
          return {
            ...prev,
            status,
            uploadState: stillActive ? 'polling' : 'done',
          };
        });
      } catch {
        // silenciar errores de polling; no interrumpir
      }
    }, POLLING_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  async function handleIngest(): Promise<void> {
    if (!state.file) return;

    setState(prev => ({ ...prev, uploadState: 'uploading', errorMsg: null, lastClear: null }));

    try {
      await buildingIngestionApi.ingest(projectId, state.file, {
        clearPrevious: state.clearPrevious,
      });

      // Job encolado — pasar a polling
      const status = await buildingIngestionApi.getStatus(projectId);
      setState(prev => ({ ...prev, status, uploadState: 'polling' }));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : 'Error al iniciar la ingesta');
      setState(prev => ({ ...prev, uploadState: 'error', errorMsg: msg }));
    }
  }

  async function handleClear(): Promise<void> {
    if (!confirm('¿Eliminar todos los centroides y asociaciones del proyecto?')) return;

    setState(prev => ({ ...prev, uploadState: 'uploading', errorMsg: null }));

    try {
      const result = await buildingIngestionApi.clear(projectId);
      const status = await buildingIngestionApi.getStatus(projectId);
      setState(prev => ({
        ...prev,
        uploadState: 'idle',
        status,
        lastClear: result,
        file: null,
      }));
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : 'Error al limpiar edificios');
      setState(prev => ({ ...prev, uploadState: 'error', errorMsg: msg }));
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0] ?? null;
    setState(prev => ({ ...prev, file, errorMsg: null }));
  }

  async function handleRefresh(): Promise<void> {
    await fetchStatus();
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const isBusy = state.uploadState === 'uploading' || state.uploadState === 'polling';
  const meta = state.status?.meta as Record<string, unknown> | null;
  const currentStatus = state.status?.status ?? 'none';
  const hasCentroids = (state.status?.centroidCount ?? 0) > 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="form-section bi-section">
      <div className="bi-header">
        <h3 className="form-section-title">Edificios</h3>
        <button
          type="button"
          className="bi-refresh-btn"
          onClick={handleRefresh}
          disabled={isBusy}
          title="Actualizar estado"
        >
          ↻
        </button>
      </div>

      {/* Estado actual */}
      <div className="bi-status-row">
        <span className="bi-status-label">Estado:</span>
        <span className={`bi-status-badge ${statusClass(currentStatus)}`}>
          {statusLabel(currentStatus)}
          {state.uploadState === 'polling' && <span className="bi-pulse" />}
        </span>
        {state.status !== null && (
          <span className="bi-centroid-count">
            {state.status.centroidCount.toLocaleString()} centroides
          </span>
        )}
      </div>

      {/* Metadata del último job */}
      {meta && (
        <div className="bi-meta">
          {typeof meta['totalRowsRead'] === 'number' && (
            <div className="bi-meta-row">
              <span>Filas leídas</span><span>{(meta['totalRowsRead'] as number).toLocaleString()}</span>
            </div>
          )}
          {typeof meta['inBounds'] === 'number' && (
            <div className="bi-meta-row">
              <span>En área</span><span>{(meta['inBounds'] as number).toLocaleString()}</span>
            </div>
          )}
          {typeof meta['inserted'] === 'number' && (
            <div className="bi-meta-row">
              <span>Insertados</span><span>{(meta['inserted'] as number).toLocaleString()}</span>
            </div>
          )}
          {typeof meta['associated'] === 'number' && (
            <div className="bi-meta-row">
              <span>Asociados a red</span><span>{(meta['associated'] as number).toLocaleString()}</span>
            </div>
          )}
          {meta['durationMs'] !== undefined && (
            <div className="bi-meta-row">
              <span>Duración</span><span>{formatMs(meta['durationMs'])}</span>
            </div>
          )}
          {typeof meta['error'] === 'string' && (
            <div className="bi-meta-row bi-meta-error">
              <span>Error</span><span>{meta['error']}</span>
            </div>
          )}
        </div>
      )}

      {/* Resultado de limpieza */}
      {state.lastClear !== null && (
        <div className="bi-clear-result">
          Eliminados: {state.lastClear.deleted} centroides, {state.lastClear.associationsDeleted} asociaciones
        </div>
      )}

      {/* Error local */}
      {state.errorMsg && (
        <div className="bi-error">{state.errorMsg}</div>
      )}

      {/* Formulario de ingesta */}
      <div className="bi-form">
        <div className="form-group">
          <label htmlFor="bi-file-input">Archivo CSV / CSV.GZ</label>
          <input
            id="bi-file-input"
            ref={fileInputRef}
            type="file"
            accept=".csv,.gz"
            onChange={handleFileChange}
            disabled={isBusy}
            className="bi-file-input"
          />
          {state.file && (
            <span className="bi-file-name">{state.file.name}</span>
          )}
        </div>

        <div className="form-group bi-checkbox-group">
          <label className="bi-checkbox-label">
            <input
              type="checkbox"
              checked={state.clearPrevious}
              onChange={e => setState(prev => ({ ...prev, clearPrevious: e.target.checked }))}
              disabled={isBusy}
            />
            <span>Limpiar datos previos antes de ingerir</span>
          </label>
        </div>

        <div className="bi-actions">
          <button
            type="button"
            className="btn-primary bi-btn"
            onClick={handleIngest}
            disabled={isBusy || !state.file}
          >
            {state.uploadState === 'uploading' ? 'Enviando...' : 'Ingerir edilicio'}
          </button>

          {hasCentroids && (
            <button
              type="button"
              className="btn-secondary bi-btn-danger"
              onClick={handleClear}
              disabled={isBusy}
            >
              Limpiar edilicio
            </button>
          )}
        </div>
      </div>

      {state.uploadState === 'polling' && (
        <p className="bi-polling-msg">
          Procesando en background — actualizando cada {POLLING_INTERVAL_MS / 1000}s...
        </p>
      )}
    </div>
  );
}
